import React from 'react';
import { useQuery } from '@apollo/client';
import { GET_NEXT_RESERVATION } from '../graphql/queries';

const formatTime = (timeString) => {
  if (!timeString) return '';

  try {
    // Handle cases where timeString is already in 12-hour format
    if (timeString.toLowerCase().includes('am') || timeString.toLowerCase().includes('pm')) {
      return timeString;
    }

    // Handle 24-hour format
    const [hours, minutes] = timeString.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) {
      return timeString; // Return original if parsing fails
    }

    const period = hours >= 12 ? 'PM' : 'AM';
    const hour12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM

    return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return timeString;
  }
};

function DueByDisplay({ job }) {


  // Skip query if we don't have job data or scheduled date
  const shouldSkip = !job || !job.property || !job.property.id || !job.scheduledDate;

  const date = job?.scheduledDate ? new Date(job.scheduledDate) : new Date();
  const dateString = date.toISOString();

  // Only fetch next reservation if we need it (timeBase is CHECKOUT)
  const needsNextReservation = job?.serviceType?.timeBase === 'CHECKOUT';

  const { data, loading, error } = useQuery(GET_NEXT_RESERVATION, {
    variables: {
      propertyId: job?.property?.id,
      date: dateString
    },
    skip: shouldSkip || !needsNextReservation,
    fetchPolicy: 'cache-and-network', // Use cache-and-network to ensure fresh data for reservation updates
    onError: (err) => {
      // Error handling without console logging
    },
    onCompleted: (data) => {
      // Processing completed without console logging
    }
  });

  if (loading || shouldSkip) {
    return <span className="text-gray-500">Loading...</span>;
  }

  // Determine what to show based on service type timeBase
  const timeBase = job?.serviceType?.timeBase;

  try {
    if (timeBase === 'CHECKOUT') {
      // Look at NEXT reservation's check-in time (for cleaning jobs)
      if (error) {
        return <span className="text-red-500">Error loading</span>;
      }

      const nextReservation = data?.nextReservationForProperty;

      if (!nextReservation) {
        return <span className="text-gray-500">No Next Check-in</span>;
      }

      // Format the next check-in date
      const checkInDate = new Date(nextReservation.check_in);
      const month = checkInDate.getMonth() + 1;
      const day = checkInDate.getDate();

      // Get check-in time
      let checkInTime;
      if (nextReservation.has_time_override && nextReservation.override_check_in) {
        // Use regex to extract time directly from ISO string to avoid timezone issues
        const timeMatch = nextReservation.override_check_in.match(/T(\d{2}):(\d{2}):(\d{2})/);
        if (timeMatch) {
          checkInTime = `${timeMatch[1]}:${timeMatch[2]}`;
        } else {
          // Fallback to Date object method
          const overrideDate = new Date(nextReservation.override_check_in);
          const hours = overrideDate.getHours().toString().padStart(2, '0');
          const minutes = overrideDate.getMinutes().toString().padStart(2, '0');
          checkInTime = `${hours}:${minutes}`;
        }
      } else {
        checkInTime = nextReservation.check_in_time ||
                     (nextReservation.property?.checkInTime) ||
                     '15:00';
      }

      const result = `${month}/${day} at ${formatTime(checkInTime)}`;
      return <span>{result}</span>;

    } else if (timeBase === 'CHECKIN') {
      // Look at CURRENT reservation's check-in time (for inspection jobs)
      const currentReservation = job.reservation;

      if (!currentReservation || !currentReservation.check_in) {
        return <span className="text-gray-500">No Current Reservation</span>;
      }

      // Format the current check-in date
      const checkInDate = new Date(currentReservation.check_in);
      const month = checkInDate.getMonth() + 1;
      const day = checkInDate.getDate();

      // Get check-in time with override support
      let checkInTime;
      if (currentReservation.has_time_override && currentReservation.override_check_in) {
        // Use regex to extract time directly from ISO string to avoid timezone issues
        const timeMatch = currentReservation.override_check_in.match(/T(\d{2}):(\d{2}):(\d{2})/);
        if (timeMatch) {
          checkInTime = `${timeMatch[1]}:${timeMatch[2]}`;
        } else {
          // Fallback to Date object method
          const overrideDate = new Date(currentReservation.override_check_in);
          const hours = overrideDate.getHours().toString().padStart(2, '0');
          const minutes = overrideDate.getMinutes().toString().padStart(2, '0');
          checkInTime = `${hours}:${minutes}`;
        }
      } else {
        checkInTime = currentReservation.check_in_time ||
                     (job.property?.checkInTime) ||
                     '15:00';
      }

      const result = `${month}/${day} at ${formatTime(checkInTime)}`;
      return <span>{result}</span>;

    } else if (timeBase === 'CUSTOM') {
      // Use job's actual start time if available, otherwise fall back to service type's default time
      const startTime = job.startTime || job.serviceType?.defaultTime || '09:00 AM';
      const scheduledDate = new Date(job.scheduledDate);
      const month = scheduledDate.getMonth() + 1;
      const day = scheduledDate.getDate();

      return <span>{month}/{day} at {formatTime(startTime)}</span>;

    } else {
      // Fallback to old behavior for backwards compatibility
      return <span className="text-gray-500">Time not configured</span>;
    }
  } catch (err) {
    return <span className="text-red-500">Format error</span>;
  }
}

// Memoize component to prevent unnecessary re-renders but ensure it updates when reservation data changes
export default React.memo(DueByDisplay, (prevProps, nextProps) => {
  // Check if job is the same object reference first (most common case)
  if (prevProps.job === nextProps.job) return true;

  // If job objects are different, check key fields
  if (prevProps.job?.id !== nextProps.job?.id) return false;
  if (prevProps.job?.scheduledDate !== nextProps.job?.scheduledDate) return false;
  if (prevProps.job?.serviceType?.timeBase !== nextProps.job?.serviceType?.timeBase) return false;

  // 🎯 CRITICAL: Check reservation override fields that affect "Due by" display
  if (prevProps.job?.reservation?.override_check_in !== nextProps.job?.reservation?.override_check_in) return false;
  if (prevProps.job?.reservation?.has_time_override !== nextProps.job?.reservation?.has_time_override) return false;
  if (prevProps.job?.reservation?.check_in_time !== nextProps.job?.reservation?.check_in_time) return false;

  // All relevant fields are the same, don't re-render
  return true;
});
